import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Copy, Check, Edit3, X } from 'lucide-react';
import { database, get, ref, set, update } from '../../firebase';

const MemberAddress = () => {
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingKey, setEditingKey] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [copiedId, setCopiedId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;

  const normalizeStatus = (value) => {
    const s = String(value || '').toLowerCase();
    if (s === 'approved') return 'Approved';
    if (s === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value || 0));

  const formatDate = (value) => {
    try {
      return new Date(value).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const snapshot = await get(ref(database, 'withdrawals'));
      const list = [];

      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data || {}).forEach(([dateKey, usersByDate]) => {
          Object.entries(usersByDate || {}).forEach(([userNodeKey, userRequests]) => {
            Object.entries(userRequests || {}).forEach(([requestId, request]) => {
              if (!request || typeof request !== 'object') return;
              const key = `${dateKey}_${userNodeKey}_${requestId}`;
              list.push({
                key,
                id: requestId,
                dateKey,
                userNodeKey,
                firebasePath: `withdrawals/${dateKey}/${userNodeKey}/${requestId}`,
                requestDate: request.requestDate || `${dateKey}T00:00:00.000Z`,
                userId: request.userId || userNodeKey,
                sponsorId: request.sponsorId || request.userId || userNodeKey,
                userName: request.userName || 'Unknown',
                walletType: request.walletType || 'N/A',
                amount: Number(request.amount || 0),
                feeAmount: Number(request.feeAmount || 0),
                netAmount: Number(request.netAmount || 0),
                status: normalizeStatus(request.status),
                currency: request?.crypto?.currency || 'N/A',
                network: request?.crypto?.network || 'N/A',
                address: request?.crypto?.address || '-',
                processedDate: request.processedDate || '',
                processedBy: request.processedBy || ''
              });
            });
          });
        });
      }

      list.sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime());
      setRows(list);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading withdrawal rows:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const snapshot = await get(ref(database, 'adminediteddetails'));
      const list = [];

      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data || {}).forEach(([dateKey, dateData]) => {
          Object.entries(dateData || {}).forEach(([id, item]) => {
            if (
              item &&
              item.sourceFile === 'MemberAddress' &&
              item.action === 'WITHDRAWAL_ADDRESS_UPDATE'
            ) {
              list.push({ id, date: dateKey, ...item });
            }
          });
        });
      }

      list.sort((a, b) => new Date(b.editedAt || b.date) - new Date(a.editedAt || a.date));
      setHistory(list);
    } catch (error) {
      console.error('Error loading edit history:', error);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchRows();
    fetchHistory();
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return rows.filter((r) => {
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      const matchSearch =
        !q ||
        String(r.sponsorId).toLowerCase().includes(q) ||
        String(r.userId).toLowerCase().includes(q) ||
        String(r.userName).toLowerCase().includes(q) ||
        String(r.address).toLowerCase().includes(q) ||
        String(r.currency).toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [rows, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage]);

  const highestAmount = useMemo(
    () => filteredRows.reduce((max, r) => (r.amount > max ? r.amount : max), 0),
    [filteredRows]
  );

  const startEdit = (row) => {
    setEditingKey(row.key);
    setEditAddress(row.address && row.address !== '-' ? row.address : '');
  };

  const cancelEdit = () => {
    setEditingKey('');
    setEditAddress('');
    setSavingKey('');
  };

  const saveAddress = async (row) => {
    const newAddress = editAddress.trim();
    if (!newAddress) return;

    try {
      setSavingKey(row.key);
      const editedAt = new Date().toISOString();
      const editedDate = editedAt.split('T')[0];
      const previousAddress = row.address || '';

      await update(ref(database, row.firebasePath), {
        crypto: {
          currency: row.currency,
          network: row.network,
          address: newAddress
        },
        lastAddressUpdated: editedAt,
        addressUpdatedBy: 'admin'
      });

      await set(ref(database, `adminediteddetails/${editedDate}/${row.sponsorId}_${Date.now()}`), {
        action: 'WITHDRAWAL_ADDRESS_UPDATE',
        sourceFile: 'MemberAddress',
        sponsorId: row.sponsorId,
        memberId: row.userId,
        memberName: row.userName,
        previousAddress,
        newAddress,
        editedBy: 'admin',
        editedAt,
        transactionDate: row.dateKey,
        transactionKey: row.id,
        transactionAmount: Number(row.amount || 0),
        transactionCurrency: row.currency
      });

      setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, address: newAddress } : r)));
      await fetchHistory();
      cancelEdit();
    } catch (error) {
      console.error('Error saving address:', error);
      setSavingKey('');
    }
  };

  const copyToClipboard = (text, id) => {
    if (!text || text === '-') return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(''), 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-card p-4 md:p-8">
        <div className="max-w-7xl mx-auto flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-card p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Withdrawal Address Report</h1>
            <p className="text-gray-400 text-sm">User side withdrawal requests with editable address</p>
          </div>
          <button
            onClick={fetchRows}
            className="px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg flex items-center gap-2 text-sm"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-card p-3 bg-gray-800/50 rounded-xl">
            <div className="text-gray-400 text-xs mb-1">Total Records</div>
            <div className="text-lg font-bold text-white">{filteredRows.length}</div>
          </div>
          <div className="glass-card p-3 bg-gray-800/50 rounded-xl">
            <div className="text-gray-400 text-xs mb-1">Total Amount</div>
            <div className="text-lg font-bold text-white">{formatCurrency(filteredRows.reduce((s, r) => s + r.amount, 0))}</div>
          </div>
          <div className="glass-card p-3 bg-gray-800/50 rounded-xl">
            <div className="text-gray-400 text-xs mb-1">Highest Amount</div>
            <div className="text-lg font-bold text-yellow-400">{formatCurrency(highestAmount)}</div>
          </div>
          <div className="glass-card p-3 bg-gray-800/50 rounded-xl">
            <div className="text-gray-400 text-xs mb-1">With Address</div>
            <div className="text-lg font-bold text-green-400">{filteredRows.filter((r) => r.address && r.address !== '-').length}</div>
          </div>
        </div>

        <div className="glass-card p-4 mb-6 bg-gray-800/50 rounded-xl flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search sponsorId, userId, userName, address..."
              className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        <div className="glass-card overflow-hidden bg-gray-800/50 rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/40 border-b border-gray-700">
                <tr>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">Date</th>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">Sponsor</th>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">User</th>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">Address</th>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">Currency</th>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">Amount</th>
                  <th className="py-3 px-4 text-left text-xs text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => (
                  <tr key={row.key} className="border-b border-gray-800/60">
                    <td className="py-3 px-4 text-xs text-gray-300">{formatDate(row.requestDate)}</td>
                    <td className="py-3 px-4 text-sm text-cyan-400">{row.sponsorId}</td>
                    <td className="py-3 px-4 text-sm text-white">{row.userName}<div className="text-xs text-gray-400">{row.userId}</div></td>
                    <td className="py-3 px-4">
                      {editingKey === row.key ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                            className="text-xs text-white bg-gray-900 border border-gray-700 px-2 py-1 rounded w-48"
                          />
                          <button onClick={() => saveAddress(row)} disabled={savingKey === row.key || !editAddress.trim()} className="p-1 hover:bg-green-500/10 rounded disabled:opacity-50">
                            <Check className="h-3 w-3 text-green-400" />
                          </button>
                          <button onClick={cancelEdit} disabled={savingKey === row.key} className="p-1 hover:bg-red-500/10 rounded disabled:opacity-50">
                            <X className="h-3 w-3 text-red-400" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <code className="text-xs text-gray-300 bg-gray-900 px-2 py-1 rounded max-w-[220px] truncate">{row.address}</code>
                          <button onClick={() => copyToClipboard(row.address, `addr-${row.key}`)} className="p-1 hover:bg-primary/10 rounded">
                            {copiedId === `addr-${row.key}` ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-gray-400" />}
                          </button>
                          <button onClick={() => startEdit(row)} className="p-1 hover:bg-primary/10 rounded"><Edit3 className="h-3 w-3 text-cyan-400" /></button>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-yellow-300">{row.currency} / {row.network}</td>
                    <td className="py-3 px-4 text-sm text-white font-semibold">{formatCurrency(row.amount)}</td>
                    <td className="py-3 px-4 text-xs text-gray-200">{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredRows.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-gray-700/50">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 text-xs rounded bg-gray-800 text-gray-200 disabled:opacity-40">Previous</button>
              <div className="text-xs text-gray-400">Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredRows.length)} of {filteredRows.length}</div>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 text-xs rounded bg-gray-800 text-gray-200 disabled:opacity-40">Next</button>
            </div>
          )}
          {filteredRows.length === 0 && <div className="p-8 text-center text-gray-400">No withdrawal address records found.</div>}
        </div>

        <div className="glass-card overflow-hidden bg-gray-800/50 rounded-xl mt-6">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Withdrawal Address Edit History</h3>
            <span className="text-xs text-gray-400">{loadingHistory ? 'Loading...' : `${history.length} records`}</span>
          </div>
          <div className="p-4">
            {loadingHistory ? (
              <div className="text-sm text-gray-400">Loading edit history...</div>
            ) : history.length === 0 ? (
              <div className="text-sm text-gray-400">No edits found from MemberAddress.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700 text-left">
                      <th className="py-2 px-3 text-xs text-gray-400">Edited At</th>
                      <th className="py-2 px-3 text-xs text-gray-400">Sponsor</th>
                      <th className="py-2 px-3 text-xs text-gray-400">Member</th>
                      <th className="py-2 px-3 text-xs text-gray-400">Previous</th>
                      <th className="py-2 px-3 text-xs text-gray-400">New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice(0, 20).map((h) => (
                      <tr key={h.id} className="border-b border-gray-800/50">
                        <td className="py-2 px-3 text-xs text-gray-300">{formatDate(h.editedAt || h.date)}</td>
                        <td className="py-2 px-3 text-xs text-cyan-400">{h.sponsorId || 'N/A'}</td>
                        <td className="py-2 px-3 text-xs text-white">{h.memberName || h.memberId || 'N/A'}</td>
                        <td className="py-2 px-3 text-xs text-red-400 font-mono">{h.previousAddress || '-'}</td>
                        <td className="py-2 px-3 text-xs text-green-400 font-mono">{h.newAddress || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberAddress;
